import { CodedProperty, NamedProperty, ResolutionProperty } from "sprint-common-web";

import {
    OnPremOrderListResponseItem,
    OnPremOrderSoundtrackListingItemResponse,
    OnPremOrderSource,
    OnPremOrderSubtitleListingItemResponse,
    OnPremOrderVideoListingItemResponse
} from "../../../common/api/orders-api/on-prem-order/GetOnPremOrderListResponse";
import {
    resolveOrderContentTitle,
    resolveOrderContentVersion
} from "../../../common/api/orders-api/order/ContentReference";
import { ContentReferenceType } from "../../../common/types/ContentReferenceType";
import { OnPremOrderStatus } from "../../common/types/OnPremOrderStatus";
import { OrderAssetAvailability } from "../../common/types/OrderAssetAvailability";
import { OrderResolutionDimensions } from "../../common/types/OrderResolutionDimensions";

export interface OnPremOrderListingItem {
    id: string;
    pONumber: string;
    content: string;
    version: string;
    video: OnPremOrderVideoListingItem;
    soundtracks: OnPremOrderSoundtrackListingItem[];
    subtitles: OnPremOrderSubtitleListingItem[];
    contentType: ContentReferenceType;
    airline: CodedProperty;
    system: NamedProperty;
    dueDate: string;
    startDate: string;
    targetResolution?: OrderResolutionDimensions;
    status: OnPremOrderStatus;
    targetFilenames: string[];
    createdDate: string;
    updatedDate: string;
    isModified?: boolean;
    isRevised?: boolean;
    isTrailer?: boolean;
    source: OnPremOrderSource;
    runtime?: string;
    isPinned?: boolean;
}

export interface OnPremOrderVideoListingItem {
    targetFilename: string;
    aspectRatio: NamedProperty;
    availability: OrderAssetAvailability;
    resolution?: ResolutionProperty;
    nonMatchingVideos: OnPremOrderNonMatchingVideoListingItem[];
}

export interface OnPremOrderNonMatchingVideoListingItem {
    assetId: string;
    subtitle: CodedProperty;
    aspectRatio: NamedProperty;
    resolution: ResolutionProperty;
}

export interface OnPremOrderSoundtrackListingItem {
    language: CodedProperty;
    availability: OrderAssetAvailability;
}

export interface OnPremOrderSubtitleListingItem {
    language: CodedProperty;
    availability: OrderAssetAvailability;
    isEmbedded: boolean;
    targetFilename?: string;
}

export const mapToOnPremOrderListingItem = (
    response: OnPremOrderListResponseItem,
    pinnedItems: string[]
): OnPremOrderListingItem => {
    return {
        id: response.id,
        content: resolveOrderContentTitle(response.content),
        version: resolveOrderContentVersion(response.content),
        airline: response.airline,
        system: response.systemPlatform.system,
        status: response.status,
        startDate: response.startDate,
        createdDate: response.audit.createdDate,
        updatedDate: response.audit.updatedDate,
        targetResolution: response.targetResolution,
        video: mapToOnPremVideoListingItem(response.onPremAssets.video),
        soundtracks: response.onPremAssets.soundtracks.map(mapToOnPremSoundtrackListingItem),
        subtitles: response.onPremAssets.subtitles.map(mapToOnPremSubtitleListingItem),
        source: response.source,
        pONumber: response.pONumber,
        contentType: response.content.type,
        dueDate: response.dueDate,
        isTrailer: response.isTrailer,
        runtime: response.runtime,
        targetFilenames: response.targetFilenames,
        isRevised: response.isRevised,
        isPinned: pinnedItems.includes(response.id)
    };
};

const mapToOnPremVideoListingItem = (video: OnPremOrderVideoListingItemResponse): OnPremOrderVideoListingItem => {
    return {
        targetFilename: video.targetFilename,
        availability: video.availability,
        resolution: video.resolution,
        aspectRatio: video.aspectRatio,
        nonMatchingVideos: video.nonMatchingVideos.map((nonMatchingVideo) => ({
            assetId: nonMatchingVideo.assetId,
            subtitle: nonMatchingVideo.subtitle,
            aspectRatio: nonMatchingVideo.aspectRatio,
            resolution: nonMatchingVideo.sourceResolution
        }))
    };
};

const mapToOnPremSoundtrackListingItem = (
    soundtrack: OnPremOrderSoundtrackListingItemResponse
): OnPremOrderSoundtrackListingItem => {
    return {
        language: soundtrack.language,
        availability: soundtrack.availability
    };
};

const mapToOnPremSubtitleListingItem = (
    subtitle: OnPremOrderSubtitleListingItemResponse
): OnPremOrderSubtitleListingItem => {
    return {
        language: subtitle.language,
        availability: subtitle.availability,
        isEmbedded: subtitle.isEmbedded,
        targetFilename: subtitle.targetFilename
    };
};
